import { IAppApiUninstall } from '../../shared/services/appApiFactory';
import { GoogleBmInstalledApp, InstallGoogleBmRequest } from '../models';
import { AgentConfigRequest } from '@/features/apps/googlebm/models/agentConfigRequest';
import { VerificationStatus } from '../models/verificationStatus';
import { LaunchStatus } from '@/features/apps/googlebm/models/launchStatus';
import { googleApi } from './googleApi';
import { EntryPointState } from '../models/entryPointState';

class GoogleBmApiService implements IAppApiUninstall {
	public entryPointStates: EntryPointState[] = [];

	public install(request: InstallGoogleBmRequest): Promise<void> {
		return googleApi.install(request);
	}

	public uninstall(workspaceId: string, installId: string): Promise<void> {
		this.entryPointStates = [];
		return googleApi.uninstall(workspaceId, installId);
	}

	public async getInstalledApp(workspaceId: string): Promise<GoogleBmInstalledApp> {
		this.entryPointStates = [];
		const installedApp = await googleApi.getInstalledApp(workspaceId);
		if (installedApp) {
			this.entryPointStates = installedApp.agents.map(agent => ({
				id: agent.customAgentId,
				launchStatus: agent.launchStatus,
				verificationStatus: agent.verificationStatus
			}));
		}

		return installedApp;
	}

	public async createAgent(workspaceId: string, request: AgentConfigRequest) {
		const agent = await googleApi.createAgent(workspaceId, request);
		this.entryPointStates.push({
			id: agent.customAgentId,
			launchStatus: agent.launchStatus,
			verificationStatus: agent.verificationStatus
		});
	}

	public updateAgent(workspaceId: string, customAgentId: string, request: AgentConfigRequest) {
		return googleApi.updateAgent(workspaceId, customAgentId, request);
	}

	public async requestAgentVerification(workspaceId: string, customAgentId: string): Promise<{ verificationState: VerificationStatus }> {
		const result = await googleApi.requestAgentVerification(workspaceId, customAgentId);

		let entryPointState = this.entryPointStates.find(state => state.id === customAgentId);

		if (entryPointState) {
			entryPointState = { ...entryPointState, verificationStatus: result.verificationState };
		}

		return result;
	}

	public async requestAgentLaunch(workspaceId: string, customAgentId: string): Promise<{ launchState: LaunchStatus }> {
		const result = await googleApi.requestAgentLaunch(workspaceId, customAgentId);

		let entryPointState = this.entryPointStates.find(state => state.id === customAgentId);

		if (entryPointState) {
			entryPointState = { ...entryPointState, launchStatus: result.launchState };
		}

		return result;
	}

	public async cancelVerification(workspaceId: string, customAgentId: string): Promise<{ verificationState: VerificationStatus }> {
		const result = await googleApi.cancelVerification(workspaceId, customAgentId);

		let entryPointState = this.entryPointStates.find(state => state.id === customAgentId);

		if (entryPointState) {
			entryPointState = { ...entryPointState, verificationStatus: result.verificationState };
		}

		return result;
	}

	public async unlaunchAgent(workspaceId: string, customAgentId: string): Promise<{ launchState: LaunchStatus }> {
		const result = await googleApi.unlaunchAgent(workspaceId, customAgentId);

		let entryPointState = this.entryPointStates.find(state => state.id === customAgentId);

		if (entryPointState) {
			entryPointState = { ...entryPointState, launchStatus: result.launchState };
		}

		return result;
	}

	public async requestLocationVerification(workspaceId: string, locationId: string): Promise<{ verificationState: VerificationStatus }> {
		const result = await googleApi.requestLocationVerification(workspaceId, locationId);

		let entryPointState = this.entryPointStates.find(state => state.id === locationId);

		if (entryPointState) {
			entryPointState = { ...entryPointState, verificationStatus: result.verificationState };
		}

		return result;
	}

	public async requestLocationLaunch(workspaceId: string, locationId: string): Promise<{ launchState: LaunchStatus }> {
		const result = await googleApi.requestLocationLaunch(workspaceId, locationId);

		let entryPointState = this.entryPointStates.find(state => state.id === locationId);

		if (entryPointState) {
			entryPointState = { ...entryPointState, launchStatus: result.launchState };
		}

		return result;
	}

	public async unlaunchLocation(workspaceId: string, locationId: string): Promise<{ launchState: LaunchStatus }> {
		const result = await googleApi.unlaunchLocation(workspaceId, locationId);

		let entryPointState = this.entryPointStates.find(state => state.id === locationId);

		if (entryPointState) {
			entryPointState = { ...entryPointState, launchStatus: result.launchState };
		}

		return result;
	}

	public canUninstall(): boolean {
		if (this.entryPointStates.length === 0) {
			return true;
		}
		return this.entryPointStates.every(state => state.verificationStatus === VerificationStatus.VERIFICATION_STATE_UNSPECIFIED);
	}

	public getUninstallNotAllowedMessage(): string {
		return 'You are unable to uninstall the app as it contains an agent that has been verified or attempted verification.';
	}
}

export const googleBmApi = new GoogleBmApiService();
